/**
 * F11 — Modal para trasladar una membresía a otro club (solo admins).
 *
 * El traslado crea el doc del club destino y borra el del origen (docId
 * determinístico `{clubId}__{personId}`), cuadra contadores de ambos clubes y
 * deja historial. Ver memberService.transferToClub.
 */
import { useState } from "react";
import { Button } from "@/components/ui";
import { X, ArrowRightLeft, Loader2 } from "lucide-react";
import { useClubs } from "@/hooks/useOrganization";
import { useTransferMember } from "@/hooks/usePersons";
import { useAuthStore } from "@/stores/authStore";
import type { Member } from "@/types";

interface TransferMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: Member;
  onSuccess?: () => void;
}

export default function TransferMemberModal({
  isOpen,
  onClose,
  member,
  onSuccess,
}: TransferMemberModalProps) {
  const { user } = useAuthStore();
  const { data: clubs = [] } = useClubs();
  const transferMutation = useTransferMember();
  const [targetClubId, setTargetClubId] = useState("");
  const [reason, setReason] = useState("");

  if (!isOpen) return null;

  // Las fusiones NO son destino de traslado: a una fusión se entra con la
  // membresía ADITIVA (FusionPanel), nunca sacando a la persona de su club.
  const candidates = clubs.filter(
    (c) => c.id !== member.clubId && c.status === "active" && !c.isFusion,
  );

  const handleTransfer = async () => {
    if (!user || !targetClubId) return;
    await transferMutation.mutateAsync({
      memberId: member.id,
      targetClubId,
      userId: user.uid,
      reason: reason.trim() || undefined,
    });
    onSuccess?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-purple-600" />
            Trasladar de club
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          {member.personName || "Este miembro"} se moverá con su historial de
          membresía. Las inscripciones a eventos ya realizadas quedan en el club
          actual como historial.
        </p>

        <label className="block text-sm font-medium text-gray-700 mb-1">
          Club destino *
        </label>
        <select
          value={targetClubId}
          onChange={(e) => setTargetClubId(e.target.value)}
          className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 mb-3"
        >
          <option value="">Selecciona un club…</option>
          {candidates.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-1">
          Motivo (opcional)
        </label>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ej: cambio de ciudad, retorno a su club de origen…"
          className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 mb-5"
        />

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleTransfer}
            disabled={!targetClubId || transferMutation.isPending}
          >
            {transferMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-1" />
            ) : (
              <ArrowRightLeft className="w-4 h-4 mr-1" />
            )}
            Trasladar
          </Button>
        </div>
      </div>
    </div>
  );
}
